/**
 * BONJIL — Quiz Data & Recommendation Engine
 * Single source of truth for all question IDs, options, and algorithm rules.
 * Loaded before Alpine (see scripts__deferred-tags.liquid).
 */

window.BONJIL_QUIZ = (function () {

  // ── Question definitions ──────────────────────────────────────
  const QUESTIONS = [
    // SECTION 1: Your Stress (Q1–Q5)
    {
      id: 'q1_first_name', num: 1, section: 1, type: 'text', required: true,
      title: "First, what's your first name?",
      subtitle: "One detail. We'll use it on your results page.",
    },
    {
      id: 'q2_stress_level', num: 2, section: 1, type: 'slider', required: true,
      title: 'On a scale of 1–10, how stressed are you now?',
      // Zones: 1–4 = maintenance, 5–7 = you need this, 8–10 = urgent
    },
    {
      id: 'q3_body_locations', num: 3, section: 1, type: 'multi', required: false,
      title: 'Where does your stress live in your body?',
      subtitle: 'Pick all that apply.',
      options: [
        { value: 'jaw',       label: 'Jaw — I clench, grind, hold tension' },
        { value: 'shoulders', label: 'Shoulders and neck — always tight' },
        { value: 'chest',     label: 'Chest — shallow breathing, tightness' },
        { value: 'stomach',   label: 'Stomach — knots, nausea, gut issues' },
        { value: 'everywhere',label: 'It\'s everywhere' },
      ],
    },
    {
      id: 'q4_stress_visible', num: 4, section: 1, type: 'single', required: false,
      title: 'What does your stress look like? Not feel like — look like.',
      options: [
        { value: 'exhausted',  label: 'I look exhausted even when I\'m not' },
        { value: 'breaking_out', label: 'My skin is breaking out or reacting' },
        { value: 'older',      label: 'I look older than I am' },
        { value: 'fine_outside', label: 'I look fine. But I\'m more depleted than I let on.' },
      ],
    },
    {
      id: 'q5_self_care_last', num: 5, section: 1, type: 'single', required: false,
      title: "When's the last time you did something for you?",
      options: [
        { value: 'today',   label: 'Today' },
        { value: 'yesterday',   label: 'Yesterday' },
        { value: 'last_week',    label: 'Last week' },
        { value: 'cant_remember', label: "I can't remember" },
        { value: 'guilty',  label: "I feel guilty even thinking about it" },
      ],
      // result_logic: if cant_remember or guilty → "This isn't selfish. It's medicine."
    },

    // SECTION 2: Your Sleep (Q6–Q8)
    {
      id: 'q6_sleep_quality', num: 6, section: 2, type: 'single', required: false,
      title: 'How are you sleeping?',
      options: [
        { value: 'well',         label: 'Well — I fall asleep easily and wake up rested' },
        { value: 'wake_middle',  label: 'Okay — but I wake up in the middle of the night' },
        { value: 'badly',        label: "Badly — racing thoughts, can't shut off" },
        { value: 'still_tired',  label: 'I sleep but wake up just as tired' },
      ],
    },
    {
      id: 'q7_bedtime_routine', num: 7, section: 2, type: 'single', required: false,
      title: 'What does the hour before bed look like?',
      options: [
        { value: 'screens',    label: 'Phone, scrolling, screens until I pass out' },
        { value: 'trying',     label: "I try to wind down but my mind won't stop" },
        { value: 'routine_failing', label: "I have a routine but it's not working" },
        { value: 'protected',  label: 'I protect that time. It\'s sacred' },
      ],
      // result_logic: if screens → "What if you replaced the scroll with a key that winds?"
    },
    {
      id: 'q8_wake_feeling', num: 8, section: 2, type: 'single', required: false,
      title: "When you wake up, what's the first thing you feel?",
      options: [
        { value: 'dread',    label: 'Dread' },
        { value: 'anxious',  label: 'Anxious — already running through the to-do list' },
        { value: 'groggy',   label: 'Groggy but okay' },
        { value: 'ready',    label: 'Ready' },
      ],
      // result_logic: if dread or anxious → "The first 5 minutes set the nervous system for the day."
    },

    // SECTION 3: Your Skin Under Stress (Q9–Q13)
    {
      id: 'q9_stress_skin_link', num: 9, section: 3, type: 'single', required: false,
      title: 'Do you think stress is affecting your skin?',
      options: [
        { value: 'absolutely',  label: 'Absolutely — I can see it' },
        { value: 'probably',    label: "Probably, but I haven't thought about it that way" },
        { value: 'not_sure',    label: "I'm not sure" },
        { value: 'mind_not_skin', label: "My skin is fine — it's my mind that's struggling" },
      ],
    },
    {
      id: 'q10_skin_type', num: 10, section: 3, type: 'single', required: false,
      title: 'How would you describe your skin right now?',
      options: [
        { value: 'oily',       label: 'Oily or congested' },
        { value: 'dry',        label: 'Dry, tight, dehydrated' },
        { value: 'sensitive',  label: 'Sensitive, reactive' },
        { value: 'dull',       label: 'Dull, tired-looking' },
        { value: 'aging',      label: 'Aging faster than it should' },
      ],
    },
    {
      id: 'q11_routine', num: 11, section: 3, type: 'single', required: false,
      title: "What's your current skincare routine?",
      options: [
        { value: 'nothing',      label: 'Almost nothing' },
        { value: 'inconsistent', label: 'A few products but inconsistent' },
        { value: 'full',         label: 'Full routine' },
        { value: 'tried_all',    label: "I've tried everything and nothing works" },
      ],
      // result_logic: tried_all → "You've been treating the symptom. This treats the cause."
      // result_logic: nothing → "This is everything you need."
    },
    {
      id: 'q12_skin_symptom', num: 12, section: 3, type: 'single', required: true,
      title: 'Where is stress showing up on your skin?',
      subtitle: 'Pick the one that resonates most.',
      hero_hint: '⭐ This chooses your hero serum',
      options: [
        { value: 'firmness',  label: 'Loss of firmness — sagging, losing structure' },
        { value: 'lines',     label: 'Lines and wrinkles — stress is etching itself into my face' },
        { value: 'dryness',   label: 'Chronic dryness — parched no matter what I use' },
        { value: 'dullness',     label: 'Dullness and uneven tone — I look faded, the light went out' },
        { value: 'sensitivity', label: 'Sensitivity and redness — my skin reacts to everything' },
      ],
    },
    {
      id: 'q13_duration', num: 13, section: 3, type: 'single', required: false,
      title: 'How long has this been going on?',
      options: [
        { value: 'recent',     label: 'Recent — last few months' },
        { value: 'year',       label: 'A year or so' },
        { value: 'years',      label: 'Years' },
        { value: 'always',     label: "I don't remember it being any other way" },
      ],
      // result_logic: years or always → "Years of cortisol don't reverse in a day..."
    },

    // SECTION 4: Your Ritual (Q14–Q22)
    {
      id: 'q14_told_to_slow', num: 14, section: 4, type: 'single', required: false,
      title: 'Has anyone — therapist, friend, doctor — ever told you to "slow down"?',
      options: [
        { value: 'yes_constantly', label: 'Yes. Constantly.' },
        { value: 'yes_never_do',   label: "Yes, but I never actually do it" },
        { value: 'know_but_no_practice', label: "I know I should but I don't have a practice" },
        { value: 'have_practice',  label: 'I have a mindfulness practice already' },
      ],
    },
    {
      id: 'q15_nervous_system_need', num: 15, section: 4, type: 'single', required: false,
      title: 'What does your nervous system need right now?',
      options: [
        { value: 'permission_stop',   label: 'Permission to stop' },
        { value: 'force_present',     label: 'Something that forces me to be present' },
        { value: 'reset',             label: 'A reset — just 5 minutes to come back to myself' },
        { value: 'beautiful_passive', label: "Something beautiful that doesn't ask anything of me" },
      ],
    },
    {
      id: 'q16_self_care_trust', num: 16, section: 4, type: 'single', required: false,
      title: 'How much do you trust yourself to follow through on self-care?',
      options: [
        { value: 'great',       label: "I'm great at it" },
        { value: 'fall_off',    label: 'I start strong but fall off' },
        { value: 'buy_unused',  label: 'I buy things and never use them' },
        { value: 'others_first',label: 'I put everyone else first' },
      ],
    },
    {
      id: 'q17_exhale_vision', num: 17, section: 4, type: 'single', required: true,
      title: 'When you imagine finally exhaling — really letting go — what does that look like?',
      hero_hint: '⭐ This chooses your Alcove tier',
      options: [
        { value: 'simple_quiet',     label: 'Simple and quiet. A candle. Silence. Nothing.' },
        { value: 'full_immersion',   label: 'Full immersion. Bath, music, low lights, the whole mood.' },
      ],
      // simple_quiet → N°1 (top-opening, winding key, ambient light)
      // full_immersion → N°2 (double-door, Bluetooth, ambient light)
    },
    {
      id: 'q18_safe_space', num: 18, section: 4, type: 'single', required: false,
      title: 'Do you have a space in your home where you can actually decompress?',
      options: [
        { value: 'yes_curated',    label: "Yes — I have somewhere I go to reset" },
        { value: 'bathroom',       label: 'Kind of — I haven\'t yet, but I want to' },
        { value: 'not_really',     label: 'No — everywhere feels like it belongs to something else' },
      ],
      // result_logic: not_really → "Your Alcove becomes that place."
    },
    {
      id: 'q19_calming_texture', num: 19, section: 4, type: 'single', required: false,
      title: 'What textures calm you down?',
      options: [
        { value: 'cool_smooth',    label: 'Cool and smooth — stone, glass, ceramic' },
        { value: 'warm_soft',      label: 'Warm and soft — fabric, wool' },
        { value: 'heavy_grounding',label: 'Heavy and grounding — weighted blankets, pressure' },
        { value: 'never_thought',  label: 'Never thought about it' },
      ],
    },
    {
      id: 'q20_safe_color', num: 20, section: 4, type: 'visual', required: true,
      title: 'What color makes you feel safe?',
      subtitle: 'Choose a mood, not a shade.',
      hero_hint: '⭐ This chooses your Alcove color',
      options: [
        { value: 'warm_golden', label: 'Warm, golden, honeyed light',  color: '#C9974A' },
        { value: 'cool_ocean',  label: 'Deep, cool, ocean-dark',       color: '#1B3A5C' },
        { value: 'clean_white', label: 'White, clean, empty space',    color: '#F0EDE8' },
        { value: 'soft_blush',  label: 'Soft, blush, being held',      color: '#DEB8A8' },
      ],
      // warm_golden → Bohemian Yellow
      // cool_ocean   → Royal Blue
      // clean_white  → Pearl White
      // soft_blush   → Rose Quartz
    },
    {
      id: 'q21_sound_effect', num: 21, section: 4, type: 'single', required: false,
      title: 'How does sound affect you?',
      options: [
        { value: 'deeply',    label: 'Deeply — music changes my entire mood' },
        { value: 'regulate',  label: 'I use it to regulate' },
        { value: 'quiet',     label: 'I mostly prefer quiet' },
      ],
      // result_logic: quiet → "It plays for 3 minutes and then leaves you in silence."
    },
    {
      id: 'q22_stress_sound', num: 22, section: 4, type: 'single', required: true,
      title: 'If stress had a sound, what would make it stop?',
      hero_hint: '⭐ This chooses your melody',
      options: [
        { value: 'structured',  label: 'Something structured and predictable — layers that ground you' },
        { value: 'sweeping',    label: 'Something that sweeps you away — flowing, romantic' },
        { value: 'delicate',    label: 'Something barely there — delicate, a whisper' },
        { value: 'cathartic',   label: 'Something that lets you feel it all then release — big, cathartic' },
      ],
      // structured → Canon (Pachelbel)
      // sweeping   → Donauwellen (Ivanovici)
      // delicate   → Chopin Waltz
      // cathartic  → Time to Say Goodbye
    },

    // SECTION 5: Closing (Q23)
    {
      id: 'q23_for_whom', num: 23, section: 5, type: 'single', required: false,
      title: 'Before we reveal your ritual, is this for you, or someone you love?',
      options: [
        { value: 'me',          label: 'Me. I need this.' },
        { value: 'loved_one',   label: "Someone I love who's struggling." },
        { value: 'gift',        label: 'Someone who would never buy this for themselves.' },
      ],
    },
  ];

  // ── Recommendation rules ──────────────────────────────────────

  // Prices for each collection's Alcove bundle (N°1 / N°2).
  // Product data (names, variant IDs, images) comes from bundle components at runtime.
  const HERO_SERUMS = {
    firmness: {
      collection: 'perfection_eternelle',
      copy: 'Reverses cortisol-driven firmness loss',
      alcove_n1: 2445,
      alcove_n2: 3445,
    },
    lines: {
      collection: 'la_signature_caviar',
      copy: 'Counteracts stress-accelerated aging',
      alcove_n1: 2200,
      alcove_n2: 3200,
    },
    dryness: {
      collection: 'luxe_hydratation',
      copy: 'Restores what cortisol dehydrates',
      alcove_n1: 2110,
      alcove_n2: 3110,
    },
    dullness: {
      collection: 'ranimer_leclat',
      copy: 'Brings back what stress took',
      alcove_n1: 2385,
      alcove_n2: 3385,
    },
    sensitivity: {
      collection: 'biomeglow',
      copy: 'Rebuilds the barrier stress broke down',
      alcove_n1: 0,
      alcove_n2: 0,
    },
  };

  const BOX_TIERS = {
    simple_quiet:   { tier: 'N°1', description: 'Top-opening, winding key, ambient light' },
    full_immersion: { tier: 'N°2', description: 'Double-door, Bluetooth, ambient light' },
  };

  const BOX_COLORS = {
    warm_golden: 'Bohemian Yellow',
    cool_ocean:  'Royal Blue',
    clean_white: 'Pearl White',
    soft_blush:  'Rose Quartz',
  };

  const MELODIES = {
    structured: 'Canon (Pachelbel)',
    sweeping:   'Donauwellen (Ivanovici)',
    delicate:   'Chopin Waltz',
    cathartic:  'Time to Say Goodbye',
  };

  // ── Public API ────────────────────────────────────────────────
  function recommend(answers) {
    const heroKey   = answers.q12_skin_symptom;
    const tierKey   = answers.q17_exhale_vision;
    const colorKey  = answers.q20_safe_color;
    const melodyKey = answers.q22_stress_sound;

    return {
      hero:     HERO_SERUMS[heroKey]    || null,
      tier:     BOX_TIERS[tierKey]      || null,
      color:    BOX_COLORS[colorKey]    || null,
      melody:   MELODIES[melodyKey]     || null,
      name:     answers.q1_first_name   || '',
      stress:   answers.q2_stress_level || 5,
    };
  }

  return {
    questions:   QUESTIONS,
    heroSerums:  HERO_SERUMS,
    boxTiers:    BOX_TIERS,
    boxColors:   BOX_COLORS,
    melodies:    MELODIES,
    recommend:   recommend,
  };

})();
